// PageIntro — the "what this page tells me" line that sits directly under a
// PageHeader / page <h1>. Two modes, one component:
//
//   1. Plain (default): an italic one-liner in muted text. Use on pages with no
//      single status to verdict on (History, Reconciliation, Vendors, etc.).
//        <PageIntro>What this page tells you, in one sentence.</PageIntro>
//
//   2. Verdict: a color-coded status banner (green = good / amber = watch /
//      red = act now) with a left accent bar. Use on the board-facing status
//      pages (Overview, Levy, Purchase Orders, Alerts) where the number + tone
//      ARE the headline. Pass `tone` to switch into verdict mode.
//        <PageIntro tone="amber">FY26 levy is tight — $24,913 available…</PageIntro>
//
// The verdict text/number is always derived by the caller from live data — this
// component never holds a figure; it only styles the sentence.

import type { ReactNode } from "react";
import { CheckCircle2, AlertTriangle, AlertOctagon } from "lucide-react";

export type VerdictTone = "green" | "amber" | "red";

interface PageIntroProps {
  children: ReactNode;
  /** When set, render as a color-coded verdict banner instead of an italic line. */
  tone?: VerdictTone;
  className?: string;
}

const TONE_BANNER: Record<VerdictTone, string> = {
  green: "bg-status-green/10 border-status-green text-theme-primary",
  amber: "bg-status-amber/10 border-status-amber text-theme-primary",
  red: "bg-status-red/10 border-status-red text-theme-primary",
};

const TONE_ICON_COLOR: Record<VerdictTone, string> = {
  green: "text-status-green",
  amber: "text-status-amber",
  red: "text-status-red",
};

const TONE_ICON: Record<VerdictTone, typeof CheckCircle2> = {
  green: CheckCircle2,
  amber: AlertTriangle,
  red: AlertOctagon,
};

export default function PageIntro({
  children,
  tone,
  className,
}: PageIntroProps) {
  if (!tone) {
    // Plain italic one-liner under the header.
    return (
      <p
        className={`mt-2 text-sm italic text-theme-secondary leading-snug ${className ?? ""}`}
      >
        {children}
      </p>
    );
  }

  const Icon = TONE_ICON[tone];
  return (
    <div
      role="status"
      className={`mt-3 flex items-start gap-2.5 rounded-lg border-l-4 px-4 py-2.5 text-sm font-medium leading-snug glass ${TONE_BANNER[tone]} ${className ?? ""}`}
    >
      <Icon
        size={18}
        className={`mt-0.5 shrink-0 ${TONE_ICON_COLOR[tone]}`}
        aria-hidden="true"
      />
      <span className="min-w-0">{children}</span>
    </div>
  );
}
